import React, { useEffect, useRef, useCallback } from 'react';
import { Task, FullProject } from '../../../types';
import { ProjectSchema, TaskSchema } from '../../../schemas/validationSchemas';
import { db } from '../../../firebase';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { getCleanTaskData } from '../../../utils/taskComparison';
import { isSystemProject } from '../../../utils/idNormalizer';
import { removeUndefined } from '../useProjectPersistence';

export interface UseProjectCloudWriterProps {
  userId?: string;
  tenantId: string;
  INBOX_PROJECT_ID: string;
  COMM_PROJECT_ID: string;
  normalizeProjectId: (id?: string) => string | undefined;
  allProjectsRef: React.MutableRefObject<FullProject[]>;
  rawTasksRef: React.MutableRefObject<Task[]>;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  setIsSyncing: React.Dispatch<React.SetStateAction<boolean>>;
  setSyncError: React.Dispatch<React.SetStateAction<string | null>>;
  setLastSynced: React.Dispatch<React.SetStateAction<number | null>>;
  dirtyProjectIdsRef: React.MutableRefObject<Set<string>>;
  dirtyTaskIdsRef: React.MutableRefObject<Set<string>>;
  taskBaselinesRef: React.MutableRefObject<Map<string, Task>>;
  recentlyDeletedTaskIdsRef: React.MutableRefObject<Set<string>>;
  syncingProjectIdsRef: React.MutableRefObject<Set<string>>;
  lastSyncTimeRef: React.MutableRefObject<Map<string, number>>;
  syncedProjectTimestampsRef: React.MutableRefObject<Map<string, number>>;
  syncedTasksRef: React.MutableRefObject<Map<string, string>>;
  triggerSync: number;
}

export function useProjectCloudWriter({
  userId,
  tenantId,
  INBOX_PROJECT_ID,
  COMM_PROJECT_ID,
  normalizeProjectId,
  allProjectsRef,
  rawTasksRef,
  setAllProjects,
  setIsSyncing,
  setSyncError,
  setLastSynced,
  dirtyProjectIdsRef,
  dirtyTaskIdsRef,
  taskBaselinesRef,
  recentlyDeletedTaskIdsRef,
  syncingProjectIdsRef,
  lastSyncTimeRef,
  syncedProjectTimestampsRef,
  syncedTasksRef,
  triggerSync,
}: UseProjectCloudWriterProps) {
  const lastSnapshotTimesRef = useRef<Map<string, number>>(new Map());
  const syncQueueRef = useRef<Map<string, Promise<unknown>>>(new Map());

  const syncProjectToFirestore = useCallback(async (project: FullProject, isSilent = false): Promise<number | null> => {
    const projectId = project.id;
    const rawProjectId = projectId === 'inbox_inforum' ? INBOX_PROJECT_ID : (projectId === 'comm_internal' ? COMM_PROJECT_ID : projectId);
    const previousPromise = syncQueueRef.current.get(projectId) || Promise.resolve();

    const nextPromise = (async () => {
      try {
        await previousPromise.catch(() => {});

        const latestProject = allProjectsRef.current.find(p => {
          if (p.rawId) return p.rawId === rawProjectId;
          return p.id === rawProjectId || p.id === projectId;
        }) || project;

        syncingProjectIdsRef.current.add(projectId);
        if (!isSilent) setIsSyncing(true);

        const syncTimestamp = Date.now();
        const cleanProjectWithTasks = removeUndefined({
          ...latestProject,
          id: rawProjectId,
          userId: userId || 'unknown',
          tenantId: latestProject.tenantId || tenantId,
          lastModified: syncTimestamp
        });

        syncedProjectTimestampsRef.current.set(projectId, syncTimestamp);
        lastSyncTimeRef.current.set(projectId, Date.now());

        setAllProjects(prev => {
          return prev.map(p => {
            if (p.id === projectId || p.rawId === projectId || p.id === (project.rawId || project.id)) {
              if (p.lastModified && latestProject.lastModified && p.lastModified > latestProject.lastModified) {
                return p;
              }
              return {
                ...p,
                lastModified: syncTimestamp
              };
            }
            return p;
          });
        });

        const { tasks: projectTasks, ...projectWithoutTasks } = cleanProjectWithTasks;

        try {
          ProjectSchema.parse(projectWithoutTasks);

          const tasksToSave = projectTasks || [];
          tasksToSave.forEach((task: Task) => {
            const cleanTask = removeUndefined({
              ...task,
              projectId,
              userId: latestProject.userId || userId || 'unknown',
              tenantId: latestProject.tenantId || task.tenantId || tenantId
            });
            TaskSchema.parse(cleanTask);
          });
        } catch (validationErr: unknown) {
          const errDetail = validationErr && typeof validationErr === 'object' && 'errors' in validationErr
            ? (validationErr as { errors: unknown }).errors
            : validationErr;
          console.error('[Sync Validation Error] Zod validation failed details:', errDetail);
          setSyncError('Validierungsfehler: Ungültige Datenstruktur');
          dirtyProjectIdsRef.current.delete(projectId);
          const normPrjId = normalizeProjectId(projectId);
          if (normPrjId) dirtyProjectIdsRef.current.delete(normPrjId);
          return null;
        }

        // Parallel Save: Local SQLite (if available)
        try {
          const localRes = await fetch('/api/projects', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cleanProjectWithTasks)
          });
          if (!localRes.ok) {
            throw new Error('SQLite backup failed');
          }
        } catch (err) {
          console.warn('Local sync failed (optional):', err);
        }

        // Cloud Save: Firestore (short-circuited in local E2E emulator mode without Java daemon)
        if (userId === 'e2e_local_uid') {
          setLastSynced(Date.now());
          setSyncError(null);
          dirtyProjectIdsRef.current.delete(projectId);
          const normPrjId = normalizeProjectId(projectId);
          if (normPrjId) dirtyProjectIdsRef.current.delete(normPrjId);
          return cleanProjectWithTasks.lastModified;
        }
        const tasksToSave = projectTasks || [];
        const cache = syncedTasksRef.current;

        const preparedTaskWrites: { task: Task; cleanTask: Task; taskStr: string; idStr: string }[] = [];
        tasksToSave.forEach((task: Task) => {
          const isCommContainer = rawProjectId.startsWith('comm_internal') || projectId === 'comm_internal';
          const effectiveTaskProjectId =
            isCommContainer && task.projectId && !isSystemProject(task.projectId) && task.type !== 'contact' && task.status !== 'pool'
              ? task.projectId
              : rawProjectId;
          const cleanTask = removeUndefined({
            ...task,
            projectId: effectiveTaskProjectId,
            userId: latestProject.userId || userId || 'unknown',
            tenantId: latestProject.tenantId || task.tenantId || tenantId,
            updatedAt: task.updatedAt || syncTimestamp
          });

          const cleanData = getCleanTaskData(cleanTask);
          const taskStr = JSON.stringify(cleanData);
          const idStr = task.id.toString();
          const isDirty = dirtyTaskIdsRef.current.has(idStr);
          const cachedStr = cache.get(idStr);

          if (taskStr !== cachedStr || isDirty) {
            dirtyTaskIdsRef.current.add(idStr);
            const rawIdx = rawTasksRef.current.findIndex(rt => String(rt.id) === idStr);
            if (rawIdx >= 0) {
              rawTasksRef.current[rawIdx] = cleanTask;
            } else {
              rawTasksRef.current.push(cleanTask);
            }
            preparedTaskWrites.push({ task, cleanTask, taskStr, idStr });
          }
        });

        const docRef = doc(db, 'projects', rawProjectId);
        const projectWritePromise = setDoc(docRef, projectWithoutTasks);

        const tasksPromises = preparedTaskWrites.map(async ({ cleanTask, taskStr, idStr }) => {
          cache.set(idStr, taskStr);
          const taskDocRef = doc(db, 'tasks', idStr);
          taskBaselinesRef.current.set(idStr, cleanTask);
          try {
            await setDoc(taskDocRef, cleanTask, { merge: true });
          } finally {
            dirtyTaskIdsRef.current.delete(idStr);
          }
        });

        const projectTasksIds = new Set(tasksToSave.map((t: Task) => t.id.toString()));

        const allOtherProjectsTaskIds = new Set<string>();
        allProjectsRef.current.forEach(p => {
          if (normalizeProjectId(p.id) !== projectId && normalizeProjectId(p.rawId || p.id) !== projectId) {
            (p.tasks || []).forEach(t => allOtherProjectsTaskIds.add(t.id.toString()));
          }
        });

        const deletedTasks = rawTasksRef.current.filter(t =>
          normalizeProjectId(t.projectId) === projectId &&
          !projectTasksIds.has(t.id.toString()) &&
          !allOtherProjectsTaskIds.has(t.id.toString())
        );

        if (deletedTasks.length > 0) {
          const deletedSet = new Set(deletedTasks.map((t) => t.id.toString()));
          rawTasksRef.current = rawTasksRef.current.filter((t) => !deletedSet.has(t.id.toString()));
        }

        const deletePromises = deletedTasks.map(async (task) => {
          const idStr = task.id.toString();
          cache.delete(idStr);
          dirtyTaskIdsRef.current.delete(idStr);
          taskBaselinesRef.current.delete(idStr);
          recentlyDeletedTaskIdsRef.current.add(idStr);
          setTimeout(() => {
            recentlyDeletedTaskIdsRef.current.delete(idStr);
          }, 15000);
          const taskDocRef = doc(db, 'tasks', idStr);
          await deleteDoc(taskDocRef);
        });

        await Promise.all([projectWritePromise, ...tasksPromises, ...deletePromises]);

        // Non-blocking Time Machine Snapshot (throttled to at most once per 2 minutes per project)
        const now = Date.now();
        const lastSnapshot = lastSnapshotTimesRef.current.get(rawProjectId) || 0;
        if (now - lastSnapshot >= 120000) {
          lastSnapshotTimesRef.current.set(rawProjectId, now);
          (async () => {
            try {
              const versionsColRef = collection(db, 'projects', rawProjectId, 'versions');
              const versionDocRef = doc(versionsColRef);

              const latestChange = latestProject.changelog && latestProject.changelog.length > 0
                ? latestProject.changelog[latestProject.changelog.length - 1]
                : null;
              const changeDesc = latestChange ? latestChange.description : 'Projekt synchronisiert';

              const snapshotData = {
                id: versionDocRef.id,
                timestamp: now,
                projectWithoutTasks,
                tasks: projectTasks || [],
                changelogDescription: changeDesc,
                author: userId || 'unknown'
              };

              await setDoc(versionDocRef, snapshotData);

              const versionsQuery = query(versionsColRef, orderBy('timestamp', 'desc'));
              const versionsSnap = await getDocs(versionsQuery);
              if (versionsSnap.size > 20) {
                const docsToDelete = versionsSnap.docs.slice(20);
                const prunePromises = docsToDelete.map(d => deleteDoc(d.ref));
                await Promise.all(prunePromises);
              }
            } catch (versionErr) {
              console.warn('[VersionControl] Non-critical snapshot warning:', versionErr);
            }
          })();
        }

        setAllProjects(prev => {
          return prev.map(p => {
            if (p.id === projectId || p.rawId === projectId || p.id === (project.rawId || project.id)) {
              if (p.lastModified && syncTimestamp && p.lastModified > syncTimestamp) {
                return p;
              }
              if (p.lastModified === cleanProjectWithTasks.lastModified && p.tasks === projectTasks) {
                return p;
              }
              return {
                ...p,
                lastModified: cleanProjectWithTasks.lastModified,
                tasks: projectTasks || []
              };
            }
            return p;
          });
        });

        setLastSynced(Date.now());
        setSyncError(null);
        dirtyProjectIdsRef.current.delete(projectId);
        const normPrjId = normalizeProjectId(projectId);
        if (normPrjId) dirtyProjectIdsRef.current.delete(normPrjId);
        return cleanProjectWithTasks.lastModified;
      } catch (err: unknown) {
        console.error('Firestore sync error details:', err);
        const errObj = err as { code?: string; message?: string } | null;
        const errCode = (errObj?.code || '').toLowerCase();
        const errMessage = (errObj?.message || '').toLowerCase();
        const isPermanentError =
          errCode === 'resource-exhausted' ||
          errCode === 'permission-denied' ||
          errCode === 'invalid-argument' ||
          errMessage.includes('too large') ||
          errMessage.includes('permission-denied') ||
          errMessage.includes('insufficient permissions') ||
          errMessage.includes('validation') ||
          errMessage.includes('invalid argument');

        if (isPermanentError) {
          dirtyProjectIdsRef.current.delete(projectId);
          const normPrjId = normalizeProjectId(projectId);
          if (normPrjId) dirtyProjectIdsRef.current.delete(normPrjId);
          if (errCode === 'resource-exhausted' || errMessage.includes('too large')) {
            setSyncError('Projekt zu groß für Cloud-Speicher (Limit 1MB)');
          } else if (errCode === 'permission-denied' || errMessage.includes('permission-denied') || errMessage.includes('insufficient permissions')) {
            setSyncError('Keine Berechtigung zum Speichern (Sicherheitsregeln)');
          } else {
            setSyncError(`Sync-Fehler: ${errObj?.message || 'Ungültige Anforderung'}`);
          }
        } else if (typeof navigator !== 'undefined' && !navigator.onLine) {
          setSyncError('Offline: Änderungen lokal gespeichert');
        } else {
          console.warn('[Sync] Transient sync warning (local cache retained):', err);
        }
        return null;
      } finally {
        syncingProjectIdsRef.current.delete(projectId);
        if (!isSilent) setIsSyncing(false);
      }
    })();

    syncQueueRef.current.set(projectId, nextPromise);
    return nextPromise;
  }, [
    userId,
    tenantId,
    INBOX_PROJECT_ID,
    COMM_PROJECT_ID,
    normalizeProjectId,
    allProjectsRef,
    rawTasksRef,
    setAllProjects,
    setIsSyncing,
    setSyncError,
    setLastSynced,
    dirtyProjectIdsRef,
    dirtyTaskIdsRef,
    taskBaselinesRef,
    recentlyDeletedTaskIdsRef,
    syncingProjectIdsRef,
    lastSyncTimeRef,
    syncedProjectTimestampsRef,
    syncedTasksRef,
  ]);

  // Debounced Batch Sync Effect
  useEffect(() => {
    if (dirtyProjectIdsRef.current.size === 0) return;

    const timer = setTimeout(async () => {
      const idsToSync = Array.from(dirtyProjectIdsRef.current);
      if (idsToSync.length === 0) return;

      setIsSyncing(true);

      try {
        await Promise.all(idsToSync.map(async (id) => {
          const normalizedId = id === 'inbox_inforum' ? INBOX_PROJECT_ID : (id === 'comm_internal' ? COMM_PROJECT_ID : id);
          const project = allProjectsRef.current.find(p => (p.rawId || p.id) === normalizedId || p.id === id || p.rawId === id);
          if (project) {
            const newTimestamp = await syncProjectToFirestore(project, true);

            const currentProject = allProjectsRef.current.find(p => (p.rawId || p.id) === normalizedId || p.id === id || p.rawId === id);
            if (currentProject && newTimestamp && currentProject.lastModified === newTimestamp) {
              dirtyProjectIdsRef.current.delete(id);
              const normId = normalizeProjectId(id);
              if (normId) dirtyProjectIdsRef.current.delete(normId);
            }
          }
        }));
      } catch (err) {
        console.error('[Sync] Batch sync error:', err);
      } finally {
        setIsSyncing(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [triggerSync, syncProjectToFirestore, INBOX_PROJECT_ID, COMM_PROJECT_ID, allProjectsRef, dirtyProjectIdsRef, normalizeProjectId, setIsSyncing]);

  return { syncProjectToFirestore };
}
